import PropTypes from 'prop-types'
import { Fragment, useEffect, useState } from 'react'
import { Box, Button, Card, CardBody, CardHeader, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Image, Input, Select, Text, useDisclosure, VStack } from '@chakra-ui/react'
import { getProjectProductionPOsByProjectId, getProjectShippingPOsByProjectId, getProjectInstallerPOsByProjectId, getProjectSalesperson, generateProjectShippingLabel, updateProject, getUsersByRoles, getUserById } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { getAssetUrl, PROJECT_ACCOUNTING_APPROVAL, PROJECT_ARTWORK, PROJECT_BALANCE_TERMS, PROJECT_CLIENT_VOLTAGE, PROJECT_CRATING_STATUS, PROJECT_DEPOSIT_STATUS, PROJECT_FABREND, PROJECT_FLIGHT_CHECK, PROJECT_PERMIT, PROJECT_POST_INSTALL_CALL, PROJECT_STAGES, ROLES } from 'lib'
import { ProjectDetailGenericCustomer } from './ProjectDetailGenericCustomer'
import { ProjectDetailGenericShipToInfo } from './ProjectDetailGenericShipToInfo'
// import { MdCheckCircleOutline, MdClose } from 'react-icons/md'
import {
  ModalProjectProductionPOType, ModalProjectProductionPO,
  ModalProjectShippingPOFormType, ModalProjectShippingPOForm,
  ModalProjectInstallerPOFormType, ModalProjectInstallerPOForm, ModalProjectInstallerPOApprove, ModalProjectInstallerPOApproved
} from './pos'
import { format as formatDate } from 'date-fns'
import { LoadingOverlay, ModalPasswordConfirmation, UiSelect } from 'components/common'
import { ModalPasswordConfirmationOnProjectCancel } from 'components/common/modal/ModalPasswordConfirmationOnProjectCancel'

export const ProjectDetailGeneric = ({ projectId, project, loadProject, ...rest }) => {
  const { createToast } = useAppContext()
  const [salesperson, setSalesperson] = useState(null)
  const [pm, setPm] = useState(null)
  const [isUpdatingPm, setIsUpdatingPm] = useState(false)
  const [projectProductionPOs, setProjectProductionPOs] = useState([])
  const [selectedProductionPO, setSelectedProductionPO] = useState({
    type: ModalProjectProductionPOType.Add,
    po: null
  })
  const [projectShippingPOs, setProjectShippingPOs] = useState([])
  const [selectedShippingPO, setSelectedShippingPO] = useState({
    type: ModalProjectShippingPOFormType.Add,
    po: null
  })
  const [projectInstallerPOs, setProjectInstallerPOs] = useState([])
  const [selectedInstallerPO, setSelectedInstallerPO] = useState({
    type: ModalProjectInstallerPOFormType.Add,
    po: null
  })
  const disclosureProductionPO = useDisclosure()
  const disclosureShippingPO = useDisclosure()
  const disclosureInstallerPO = useDisclosure()
  const disclosureInstallerPOApprove = useDisclosure()
  const disclosureInstallerPOApproved = useDisclosure()
  const disclosureOverlayProcessing = useDisclosure()
  const disclosureConfirmModalOnComplete = useDisclosure()
  const disclosureConfirmModalOnCancel = useDisclosure()

  const loadProjectSalesperson = async () => {
    try {
      const salesperson = await getProjectSalesperson(projectId)
      setSalesperson(salesperson)
    } catch (err) {
      createToast('error', 'Error', err.response.data)
    }
  }

  const loadProjectManager = async () => {
    try {
      const pm = project?.pmId ? await getUserById(project.pmId) : null
      setPm(pm)
    } catch (err) {
      createToast('error', 'Error', err.response.data)
    }
  }

  const loadProjectProductionPOs = async () => {
    try {
      const projectProductionPOs = await getProjectProductionPOsByProjectId(projectId)
      setProjectProductionPOs(projectProductionPOs)
    } catch (err) {
      createToast('error', 'Error', err)
    }
  }

  const loadProjectShippingPOs = async () => {
    try {
      const projectShippingPOs = await getProjectShippingPOsByProjectId(projectId)
      setProjectShippingPOs(projectShippingPOs)
    } catch (err) {
      createToast('error', 'Error', err)
    }
  }

  const loadProjectInstallerPOs = async () => {
    try {
      const projectInstallerPOs = await getProjectInstallerPOsByProjectId(projectId)
      setProjectInstallerPOs(projectInstallerPOs)
    } catch (err) {
      createToast('error', 'Error', err)
    }
  }

  useEffect(() => {
    if (!project?.id) {
      return
    }

    loadProjectSalesperson()
    loadProjectManager()
    loadProjectProductionPOs()
    loadProjectShippingPOs()
    loadProjectInstallerPOs()
  }, [project?.id])

  const handleFieldChange = async (e) => {
    disclosureOverlayProcessing.onOpen()
    try {
      await updateProject(projectId, { [e.target.name]: e.target.value })
      createToast('success', 'Success', `Sucessfully updated field - ${e.target.name}`)
      disclosureOverlayProcessing.onClose()
      loadProject()
    } catch (err) {
      createToast('error', 'Error', `Failed to update field - ${e.target.name}`)
      disclosureOverlayProcessing.onClose()
    }
  }

  const onAddProductionPO = () => {
    setSelectedProductionPO({
      type: ModalProjectProductionPOType.Add,
      po: null
    })
    disclosureProductionPO.onOpen()
  }

  const onEditProductionPO = (po) => {
    setSelectedProductionPO({
      type: ModalProjectProductionPOType.Edit,
      po: { ...po }
    })
    disclosureProductionPO.onOpen()
  }

  const onAddShippingPO = () => {
    setSelectedShippingPO({
      type: ModalProjectShippingPOFormType.Add,
      po: null
    })
    disclosureShippingPO.onOpen()
  }

  const onEditShippingPO = (po) => {
    setSelectedShippingPO({
      type: ModalProjectShippingPOFormType.Edit,
      po: { ...po }
    })
    disclosureShippingPO.onOpen()
  }

  const onAddInstallerPO = () => {
    setSelectedInstallerPO({
      type: ModalProjectInstallerPOFormType.Add,
      po: null
    })
    disclosureInstallerPO.onOpen()
  }

  const onEditInstallerPO = (po) => {
    setSelectedInstallerPO({
      type: ModalProjectInstallerPOFormType.Edit,
      po: { ...po }
    })
    disclosureInstallerPO.onOpen()
  }

  const onApproveInstallerPO = (po) => {
    setSelectedInstallerPO({
      type: ModalProjectInstallerPOFormType.Approve,
      po: { ...po }
    })
    disclosureInstallerPOApprove.onOpen()
  }

  const onApprovedInstallerPO = (po) => {
    setSelectedInstallerPO({
      type: ModalProjectInstallerPOFormType.Approved,
      po: { ...po }
    })
    disclosureInstallerPOApproved.onOpen()
  }

  const handleClickShippingLabel = async (poId) => {
    try {
      const response = await generateProjectShippingLabel(poId)
      window.open(getAssetUrl(response.pdf))
    } catch (err) {
      createToast('error', 'Error', err)
    }
  }

  const handleProjectComplete = async () => {
    try {
      await updateProject(projectId, { stage: PROJECT_STAGES.Complete })
      createToast('success', 'Success', 'Project completed.')
      window.location.href = '/projects'
    } catch (err) {
      createToast('error', 'Error', 'Failed to complete project')
    }
  }

  const handleProjectCancel = async (reason) => {
    try {
      await updateProject(projectId, { stage: PROJECT_STAGES.Cancelled, cancelledReason: reason })
      createToast('success', 'Success', 'Project cancelled.')
      window.location.href = '/projects'
    } catch (err) {
      createToast('error', 'Error', 'Failed to cancel project')
    }
  }

  const handleUpdatePm = async () => {
    if (!pm?.id) {
      createToast('warning', 'Warning', 'Please select project manager.')
      return
    }

    setIsUpdatingPm(true)
    try {
      await updateProject(projectId, { pmId: pm.id })
      createToast('success', 'Success', 'Project manager updated.')
      setIsUpdatingPm(false)
    } catch (err) {
      createToast('error', 'Error', 'Failed to udpate project manager')
      setIsUpdatingPm(false)
    }
  }

  return (
    <>
      {disclosureOverlayProcessing.isOpen && <LoadingOverlay />}
      <Grid templateColumns="repeat(12, 1fr)" gap={16} {...rest}>
        <GridItem colSpan={4}>
          <Box>
            <FormControl>
              <FormLabel>Salesperson</FormLabel>
              <Input type="text" name="salesperson" value={`${salesperson?.firstname} ${salesperson?.lastname}`} readOnly />
            </FormControl>
          </Box>
          <Box mt={4}>
            <FormControl>
              <FormLabel>Project Manager</FormLabel>
              <UiSelect
                value={pm}
                onChange={v => {
                  setPm(v)
                }}
                loadData={(page, search) => getUsersByRoles(page, 20, search, 'name', 'asc', [ROLES.ProjectManager, ROLES.SuperUser, ROLES.SalesUserProjectManager, ROLES.Admin], true)}
                apiRespProp="rows"
                getOptionValue={v => v.id}
                getOptionLabel={v => v.name}
                placeholder="Project Managers"
                noOptionsMessage={() => 'No Users Found'}
                required={true}
              />
            </FormControl>
            <Button colorScheme="blue" size="sm" mt={2} onClick={handleUpdatePm} isLoading={isUpdatingPm}>Change Project Manager</Button>
          </Box>
          <ProjectDetailGenericCustomer
            project={project}
            reload={loadProject}
            mt={4}
          />
          <ProjectDetailGenericShipToInfo
            project={project}
            mt={4}
          />
        </GridItem>
        <GridItem colSpan={8}>
          <VStack gap={4} alignItems="none">
            <Card>
              <CardHeader bg="gray.200"><Heading size="md">Staging</Heading></CardHeader>
              <CardBody>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Pre-Pro Conference</FormLabel>
                      <Flex gap={2} alignItems="center">
                        <Select name="flightCheck" value={project?.flightCheck} onChange={handleFieldChange}>
                          {Object.keys(PROJECT_FLIGHT_CHECK).map(v =>
                            <option key={PROJECT_FLIGHT_CHECK[v]} value={PROJECT_FLIGHT_CHECK[v]}>{PROJECT_FLIGHT_CHECK[v]}</option>
                          )}
                        </Select>
                        {/* <Box color="green.400"><MdCheckCircleOutline size="24px" /></Box>
                        <Box color="red.400"><MdClose size="24px" /></Box> */}
                      </Flex>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Deposit</FormLabel>
                      <Select name="depositStatus" value={project?.depositStatus} onChange={handleFieldChange}>
                        {Object.keys(PROJECT_DEPOSIT_STATUS).map(v =>
                          <option key={PROJECT_DEPOSIT_STATUS[v]} value={PROJECT_DEPOSIT_STATUS[v]}>{PROJECT_DEPOSIT_STATUS[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Balance Terms</FormLabel>
                      <Select name="balanceTerms" value={project?.balanceTerms} onChange={handleFieldChange}>
                        {Object.keys(PROJECT_BALANCE_TERMS).map(v =>
                          <option key={PROJECT_BALANCE_TERMS[v]} value={PROJECT_BALANCE_TERMS[v]}>{PROJECT_BALANCE_TERMS[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>
              </CardBody>
            </Card>
            <Card>
              <CardHeader bg="gray.200"><Heading size="md">Pre-Production</Heading></CardHeader>
              <CardBody>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Permit</FormLabel>
                      <Select name="permit" value={project?.permit} onChange={handleFieldChange}>
                        {Object.keys(PROJECT_PERMIT).map(v =>
                          <option key={PROJECT_PERMIT[v]} value={PROJECT_PERMIT[v]}>{PROJECT_PERMIT[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Job Packet</FormLabel>
                      <Select name="fabRend" value={project?.fabRend} onChange={handleFieldChange}>
                        {Object.keys(PROJECT_FABREND).map(v =>
                          <option key={PROJECT_FABREND[v]} value={PROJECT_FABREND[v]}>{PROJECT_FABREND[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Artwork</FormLabel>
                      <Select name="artwork" value={project?.artwork} onChange={handleFieldChange}>
                        {Object.keys(PROJECT_ARTWORK).map(v =>
                          <option key={PROJECT_ARTWORK[v]} value={PROJECT_ARTWORK[v]}>{PROJECT_ARTWORK[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Client&apos;s Voltage</FormLabel>
                      <Select name="clientVoltage" value={project?.clientVoltage} onChange={handleFieldChange}>
                        {Object.keys(PROJECT_CLIENT_VOLTAGE).map(v =>
                          <option key={PROJECT_CLIENT_VOLTAGE[v]} value={PROJECT_CLIENT_VOLTAGE[v]}>{PROJECT_CLIENT_VOLTAGE[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>
              </CardBody>
            </Card>
            <Card>
              <CardHeader bg="gray.200">
                <Flex justifyContent="space-between">
                  <Heading size="md">Production</Heading>
                  <Button size="sm" colorScheme="blue" onClick={onAddProductionPO}>Add Item</Button>
                </Flex>
              </CardHeader>
              <CardBody>
                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={8}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Client ETA</FormLabel>
                      <Input type="date" name="productionClientEta" value={project?.productionClientEta ? project.productionClientEta : ''} onChange={handleFieldChange} />
                    </FormControl>
                  </GridItem>
                </Grid>
                {!projectProductionPOs.length && <Text fontSize="sm">No items.</Text>}
                <Grid templateColumns="repeat(11, 1fr)" gap={4}>
                  {projectProductionPOs.map((po) => {
                    const date = new Date(po.eta)
                    const eta = po.eta ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'MM/dd/yyyy') : 'N/A'
                    return (
                      <Fragment key={po.id}>
                        <GridItem colSpan={2}>
                          <Button size="sm" fontWeight="medium" variant="link" color="black" onClick={() => onEditProductionPO(po)}>{po.name}</Button>
                        </GridItem>
                        <GridItem colSpan={3}>
                          <Text fontSize="sm">Status: {po.status}</Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">Ordered: {formatDate(new Date(po.createdAt), 'MM/dd/yyyy')}</Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">ETA: {eta}</Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Button size="sm" fontWeight="medium" variant="link" color="black" onClick={() => po.pdf && window.open(getAssetUrl(po.pdf))}>PO#{po.po}</Button>
                        </GridItem>
                      </Fragment>
                    )
                  })}
                </Grid>
              </CardBody>
            </Card>
            <Card>
              <CardHeader bg="gray.200">
                <Flex justifyContent="space-between">
                  <Heading size="md">Shipping</Heading>
                  <Button size="sm" colorScheme="blue" onClick={onAddShippingPO}>Add Item</Button>
                </Flex>
              </CardHeader>
              <CardBody>
                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={6}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Crating Status</FormLabel>
                      <Select name="cratingStatus" value={project?.cratingStatus} onChange={handleFieldChange}>
                        {Object.keys(PROJECT_CRATING_STATUS).map(v =>
                          <option key={PROJECT_CRATING_STATUS[v]} value={PROJECT_CRATING_STATUS[v]}>{PROJECT_CRATING_STATUS[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Accounting Approval</FormLabel>
                      <Select name="accountingApproval" value={project?.accountingApproval} onChange={handleFieldChange}>
                        {Object.keys(PROJECT_ACCOUNTING_APPROVAL).map(v =>
                          <option key={PROJECT_ACCOUNTING_APPROVAL[v]} value={PROJECT_ACCOUNTING_APPROVAL[v]}>{PROJECT_ACCOUNTING_APPROVAL[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>
                {!projectShippingPOs.length && <Text fontSize="sm">No items.</Text>}
                <Grid templateColumns="repeat(12, 1fr)" gap={4}>
                  {projectShippingPOs.map((po) => {
                    const date = new Date(po.eta)
                    const eta = po.eta ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'MM/dd/yyyy') : 'N/A'
                    return (
                      <Fragment key={po.id}>
                        <GridItem colSpan={2}>
                          <Button size="sm" fontWeight="medium" variant="link" color="black" onClick={() => onEditShippingPO(po)}>{po.name}</Button>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Button size="sm" fontWeight="medium" variant="link" color="black" onClick={() => handleClickShippingLabel(po.id)}>Shipping Label</Button>
                        </GridItem>
                        <GridItem colSpan={3}>
                          <Button size="sm" fontWeight="medium" variant="link" color="black" onClick={() => po.pdf && window.open(getAssetUrl(po.pdf))}>Tracking No: {po.pro}</Button>
                          <Text fontSize="sm"></Text>
                        </GridItem>
                        <GridItem colSpan={3}>
                          <Text fontSize="sm">Booking Date: {formatDate(new Date(po.createdAt), 'MM/dd/yyyy')}</Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">ETA: {eta}</Text>
                        </GridItem>
                      </Fragment>
                    )
                  })}
                </Grid>
              </CardBody>
            </Card>
            <Card>
              <CardHeader bg="gray.200">
                <Flex justifyContent="space-between">
                  <Heading size="md">Installation</Heading>
                  <Button size="sm" colorScheme="blue" onClick={onAddInstallerPO}>Add Item</Button>
                </Flex>
              </CardHeader>
              <CardBody>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>COI/W9</FormLabel>
                      <Input name="coiw9" value={project?.coiw9 ? project.coiw9 : 'Not Received'} isReadOnly={true} />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Post Install Call</FormLabel>
                      <Select name="postInstallCall" value={project?.postInstallCall} onChange={handleFieldChange}>
                        {Object.keys(PROJECT_POST_INSTALL_CALL).map(v =>
                          <option key={PROJECT_POST_INSTALL_CALL[v]} value={PROJECT_POST_INSTALL_CALL[v]}>{PROJECT_POST_INSTALL_CALL[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Target Date</FormLabel>
                      <Input type="date" name="installDate" value={project?.installDate ? project.installDate : ''} onChange={handleFieldChange} />
                    </FormControl>
                  </GridItem>
                </Grid>
                <Grid templateColumns="repeat(10, 1fr)" gap={4} mt={8}>
                  {projectInstallerPOs.map(po => {
                    const date = new Date(po.eta)
                    const eta = po.eta ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'MM/dd/yyyy') : 'N/A'
                    return (
                      <Fragment key={po.id}>
                        <GridItem colSpan={2}>
                          <Button size="sm" fontWeight="medium" variant="link" color="black" onClick={() => { po.isApproved ? onApprovedInstallerPO(po) : onApproveInstallerPO(po) }}>{po.name}</Button>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">{po.isApproved ? 'POC Signed' : 'POC Generated'}</Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">Ordered: {formatDate(new Date(po.createdAt), 'MM/dd/yyyy')}</Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">ETA: {eta}</Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Button size="sm" fontWeight="medium" variant="link" color="black" onClick={() => po.pdf && window.open(getAssetUrl(po.pdf))}>PO: {po.po}</Button>
                        </GridItem>
                        <GridItem colSpan={2}></GridItem>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">{po.company ?? 'N/A'}</Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">{po.contact ?? 'N/A'}</Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">{po.phone ?? 'N/A'}</Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">{po.email ?? 'N/A'}</Text>
                        </GridItem>
                      </Fragment>
                    )
                  })}
                </Grid>
              </CardBody>
            </Card>
            {(project?.invocingAcquiredAction || project?.invoicingCustomerDisposition || project?.invoicingRemarks || project?.invoicingAttachments) && (
              <Card>
                <CardHeader bg="gray.200"><Heading size="md">Installation Summary</Heading></CardHeader>
                <CardBody>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Status</FormLabel>
                        <Text fontSize="sm">{project?.invocingAcquiredAction || 'N/A'}</Text>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Customer Disposition</FormLabel>
                        <Text fontSize="sm">{project?.invoicingCustomerDisposition || 'N/A'}</Text>
                      </FormControl>
                    </GridItem>
                    <GridItem colSpan={2}>
                      <FormControl>
                        <FormLabel>Remarks</FormLabel>
                        <Text fontSize="sm">{project?.invoicingRemarks || 'N/A'}</Text>
                      </FormControl>
                    </GridItem>
                  </Grid>
                  {project?.invoicingAttachments && (
                    <Box mt={6}>
                      <Text fontWeight="medium" fontSize="sm" mb={3}>Photos</Text>
                      <Flex gap={4} flexWrap="wrap">
                        {project.invoicingAttachments.split(',').filter(v => v).map(path => {
                          const ext = path.split('.').pop().toLowerCase()
                          const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
                          return isImage ? (
                            <Image
                              key={path}
                              src={getAssetUrl(path)}
                              w="120px"
                              h="120px"
                              objectFit="cover"
                              borderRadius="md"
                              border="1px solid"
                              borderColor="gray.200"
                              cursor="pointer"
                              onClick={() => window.open(getAssetUrl(path))}
                            />
                          ) : (
                            <Button
                              key={path}
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(getAssetUrl(path))}
                            >
                              {path.split('/').pop()}
                            </Button>
                          )
                        })}
                      </Flex>
                    </Box>
                  )}
                </CardBody>
              </Card>
            )}
            <Card>
              <CardBody>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Phase</FormLabel>
                      <Select name="stage" value={project?.stage} onChange={handleFieldChange}>
                        {Object.keys(PROJECT_STAGES).map(v =>
                          <option key={PROJECT_STAGES[v]} value={PROJECT_STAGES[v]}>{PROJECT_STAGES[v]}</option>
                        )}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Warranty PDF</FormLabel>
                      {project?.warrantyPdf ? (
                        <Button size="sm" onClick={() => project?.warrantyPdf && window.open(getAssetUrl(project.warrantyPdf))}>View</Button>
                      ) : (
                        <Text fontSize="sm">Not available yet</Text>
                      )}
                    </FormControl>
                  </GridItem>
                </Grid>
                <Flex justifyContent="flex-end" gap={4} mt={4}>
                  <Button colorScheme="blue" onClick={disclosureConfirmModalOnComplete.onOpen}>Complete Project</Button>
                  <Button colorScheme="yellow" onClick={disclosureConfirmModalOnCancel.onOpen}>Cancel Project</Button>
                </Flex>
              </CardBody>
            </Card>
          </VStack>
        </GridItem>
      </Grid>
      <ModalProjectProductionPO
        type={selectedProductionPO.type}
        po={selectedProductionPO.po}
        projectId={parseInt(projectId)}
        reload={loadProjectProductionPOs}
        {...disclosureProductionPO}
      />
      <ModalProjectShippingPOForm
        type={selectedShippingPO.type}
        po={selectedShippingPO.po}
        projectId={parseInt(projectId)}
        reload={loadProjectShippingPOs}
        {...disclosureShippingPO}
      />
      <ModalProjectInstallerPOForm
        type={selectedInstallerPO.type}
        po={selectedInstallerPO.po}
        projectId={parseInt(projectId)}
        reload={loadProjectInstallerPOs}
        {...disclosureInstallerPO}
      />
      <ModalProjectInstallerPOApprove
        onEdit={onEditInstallerPO}
        type={selectedInstallerPO.type}
        po={selectedInstallerPO.po}
        reload={loadProjectInstallerPOs}
        {...disclosureInstallerPOApprove}
      />
      <ModalProjectInstallerPOApproved
        po={selectedInstallerPO.po}
        reload={loadProjectInstallerPOs}
        {...disclosureInstallerPOApproved}
      />
      {disclosureConfirmModalOnComplete.isOpen && (
        <ModalPasswordConfirmation
          {...disclosureConfirmModalOnComplete}
          heading="Do you want to complete project?"
          btnConfirmLabel="Confirm"
          btnCancelLabel="Cancel"
          onConfirm={handleProjectComplete}
        />
      )}
      {disclosureConfirmModalOnCancel.isOpen && (
        <ModalPasswordConfirmationOnProjectCancel
          {...disclosureConfirmModalOnCancel}
          heading="Do you want to cancel project?"
          btnConfirmLabel="Confirm"
          btnCancelLabel="Cancel"
          onConfirm={handleProjectCancel}
        />
      )}
    </>
  )
}

ProjectDetailGeneric.propTypes = {
  projectId: PropTypes.number,
  project: PropTypes.object,
  loadProject: PropTypes.func
}
